import { Link } from 'react-router-dom';
import { Feuille, Compteur } from '../ui/kit';
import { Icon } from '../ui/Icon';
import { Media } from '../ui/Media';
import { useBoutique } from '../state/boutique';
import { eur, kg, manquePourLivraisonOfferte, fraisLivraison } from '../data/repository';
import { REGLAGES } from '../data/mock-data';

/**
 * TIROIR DU PANIER — l'endroit où l'on vérifie avant de payer.
 *
 * Trois choix de conception :
 *   · il ne s'ouvre **jamais** tout seul (pas de « mini-panier surprise ») ;
 *   · chaque ligne dit ce qu'elle contient en clair : poids, prix estimé, tolérance ;
 *   · on n'y modifie que la quantité — le poids se change sur la fiche, là où le
 *     sélecteur et le prix au kilo sont lisibles.
 */
export function CartDrawer() {
  const { panierOuvert, ouvrirPanier, lignes, nbArticles, sousTotal, total, mode, majQuantite, retirer } = useBoutique();
  const manque = manquePourLivraisonOfferte(sousTotal);
  const frais = fraisLivraison(sousTotal, mode);
  const progression = Math.min(100, (sousTotal / REGLAGES.livraison.seuilGratuite) * 100);

  return (
    <Feuille
      ouverte={panierOuvert}
      onFermer={() => ouvrirPanier(false)}
      titre={nbArticles > 0 ? `Votre panier (${nbArticles})` : 'Votre panier'}
      cote="right"
      pied={nbArticles > 0 ? (
        <div>
          {mode === 'DELIVERY' && (
            <div className="mb-4">
              <div className="mb-1.5 flex items-center justify-between text-[0.8125rem]">
                <span className="text-ink-soft">
                  {manque > 0 ? `Plus que ${eur(manque)} pour la livraison offerte` : 'Livraison offerte'}
                </span>
                {manque === 0 && <Icon name="check" size={15} className="text-success" />}
              </div>
              <div className="meter"><div className="meter__fill" style={{ width: `${progression}%` }} /></div>
            </div>
          )}
          <dl className="mb-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-ink-soft"><dt>Sous-total</dt><dd className="tabular-nums">{eur(sousTotal)}</dd></div>
            <div className="flex justify-between text-ink-soft">
              <dt>{mode === 'PICKUP' ? 'Retrait en boutique' : 'Livraison réfrigérée'}</dt>
              <dd className="tabular-nums">{frais === 0 ? 'Offert' : eur(frais)}</dd>
            </div>
            <div className="flex justify-between border-t border-linen-deep/70 pt-2 text-base font-semibold">
              <dt>Total estimé</dt><dd className="tabular-nums">{eur(total)}</dd>
            </div>
          </dl>
          <Link to="/commande" onClick={() => ouvrirPanier(false)} className="btn btn--primary btn--block btn--lg">
            Passer commande <Icon name="arrowRight" size={18} />
          </Link>
          <Link to="/panier" onClick={() => ouvrirPanier(false)}
            className="mt-2 block py-2 text-center text-sm font-medium text-ink-soft underline underline-offset-4 hover:text-ink">
            Voir le panier en détail
          </Link>
        </div>
      ) : undefined}
    >
      {nbArticles === 0 ? (
        <div className="flex h-full flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 grid h-16 w-16 place-items-center rounded-full bg-linen text-ink-soft">
            <Icon name="cart" size={26} />
          </div>
          <p className="font-display text-2xl">Votre panier est vide</p>
          <p className="mt-2 max-w-xs text-sm text-muted">
            Une côte à partager, un rôti pour dimanche, ou un colis déjà composé : le comptoir est ouvert.
          </p>
          <Link to="/boutique" onClick={() => ouvrirPanier(false)} className="btn btn--ink mt-6">
            Découvrir le comptoir
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-linen-deep/60">
          {lignes.map(l => (
            <li key={l.cle} className="flex gap-3.5 py-4">
              <Link to={`/produit/${l.produit.slug}`} onClick={() => ouvrirPanier(false)} className="shrink-0">
                <Media src={l.produit.vignette} alt={l.produit.nom} ratio="aspect-square"
                  className="h-20 w-20 rounded-xl" monogramme={l.produit.nom.slice(0, 1)} />
              </Link>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/produit/${l.produit.slug}`} onClick={() => ouvrirPanier(false)}
                    className="text-[0.9375rem] font-semibold leading-snug hover:text-wine">
                    {l.produit.nom}
                  </Link>
                  <button type="button" onClick={() => retirer(l.cle)}
                    className="shrink-0 rounded-full p-1.5 text-muted transition hover:bg-linen hover:text-danger"
                    aria-label={`Retirer ${l.produit.nom} du panier`}>
                    <Icon name="close" size={15} />
                  </button>
                </div>
                <p className="mt-0.5 text-[0.8125rem] text-muted">
                  {l.produit.modele === 'AU_KG' ? `${kg(l.poidsG)} · ±${REGLAGES.poids.tolerancePct} %` : l.produit.modele === 'FORMATS' ? `format ${kg(l.poidsG)}` : 'à la pièce'}
                  {Object.entries(l.options).length > 0 && ` · ${Object.values(l.options).join(' · ')}`}
                </p>
                <div className="mt-2.5 flex items-center justify-between gap-2">
                  <Compteur valeur={l.quantite} onChange={q => majQuantite(l.cle, q)} libelle={l.produit.nom} taille="sm" />
                  <span className="font-semibold tabular-nums">{eur(l.total)}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Feuille>
  );
}
